import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';

import type AppEventsSlice from '../../interfaces/AppEventsSlice';
import { createEventsSlice, type EventsSlice } from '../../interfaces/EventsSlice';
import type { FilesPasswordsSlice } from '../../interfaces/FilesPasswordsSlice';
import type { PublicFilesPasswordsSlice } from '../../interfaces/PublicFilesPasswordsSlice';
import type FilesThumbnailsSlice from '../../interfaces/FilesThumbnailsSlice';
import type { AlertsSlice } from '../../interfaces/AlertsSlice';
import type { FolderContentStatsSlice } from '../../interfaces/FolderContentStatsSlice';
import { createAppEventsSlice } from '../appEventsSlice';
import { createAlertsSlice } from '../alertsSlice';
import { createAuthSlice, type AuthSlice } from '../authSlice';
import { createEmailSlice, type EmailSlice } from '../emailSlice';
import { createFileDownloadSlice, type FileDownloadSlice } from '../fileDownloadSlice';
import { createFolderContentStatsSlice } from '../folderContentStatsSlice';
import { createRightPanelSlice, type RightPanelSlice } from '../rightPanelSlice';
import { createBrandingDataSlice, type BrandingDataSlice } from '../brandingDataSlice';
import { createFilesPasswordsSlice } from '../filesPasswordsSlice';
import { createPublicFilesPasswordsSlice } from '../publicFilesPasswordsSlice';
import { createFilesThumbnailsSlice } from '../filesThumbnailsSlice';
import { createGrantsSlice, type GrantsSlice } from '../grantsSlice';
import { createGroupsSlice, type GroupsSlice } from '../groupsSlice';
import { createModalSlice, type ModalSlice } from '../modalSlice';
import { createNotificationsSlice, type NotificationsSlice } from '../notificationsSlice';
import { createOfficeConfigSlice, type OfficeConfigSlice } from '../officeConfigSlice';
import { createSettingsSlice, type SettingsSlice } from '../settingsSlice';
import { createUsersSlice, type UsersSlice } from '../usersSlice';
import { createCsvUserImportSlice, type CsvUserImportSlice } from '../csvUserImportSlice';
import { createViewerModalSlice, type ViewerModalSlice } from '../viewerModalSlice';
import { createWebSocketSlice, type WebSocketSlice } from '../webSocketSlice';
import {
  mergeAppPersistedState,
  migrateAppPersistedState,
  partialAppPersistedState,
  runAppStorePersistRehydration,
} from '../storePersistence';

export type AppState = BrandingDataSlice &
  GroupsSlice &
  GrantsSlice &
  ModalSlice &
  AuthSlice &
  AppEventsSlice &
  WebSocketSlice &
  NotificationsSlice &
  EventsSlice &
  UsersSlice &
  SettingsSlice &
  OfficeConfigSlice &
  FilesPasswordsSlice &
  PublicFilesPasswordsSlice &
  FilesThumbnailsSlice &
  EmailSlice &
  AlertsSlice &
  FileDownloadSlice &
  FolderContentStatsSlice &
  RightPanelSlice &
  CsvUserImportSlice &
  ViewerModalSlice;

export const useAppStore = create<AppState>()(
  persist(
    (set, get, store) =>
      ({
        ...createBrandingDataSlice(set, get),
        ...createGroupsSlice(set),
        ...createGrantsSlice(set, get, store),
        ...createModalSlice(set),
        ...createAuthSlice(set, get),
        ...createAlertsSlice(set, get, store),
        ...createWebSocketSlice(set, get, store),
        ...createAppEventsSlice(set, get, store),
        ...createNotificationsSlice(set, get, store),
        ...createEventsSlice(set, get, store),
        ...createUsersSlice(set),
        ...createCsvUserImportSlice(set),
        ...createViewerModalSlice(set),
        ...createSettingsSlice(set, get),
        ...createOfficeConfigSlice(set, get),
        ...createFilesPasswordsSlice(set, get),
        ...createPublicFilesPasswordsSlice(set, get),
        ...createFilesThumbnailsSlice(set, get, store),
        ...createEmailSlice(set),
        ...createFileDownloadSlice(set, get, store),
        ...createFolderContentStatsSlice(set, get),
        ...createRightPanelSlice(set, get, store),
      }) as AppState,
    {
      name: 'appStore',
      merge: (persistedState, currentState) =>
        mergeAppPersistedState(persistedState, currentState) as unknown as AppState,
      storage: createJSONStorage(() => localStorage),
      version: 7,
      partialize: (s) =>
        partialAppPersistedState({
          locale: s.locale,
          colorSchemePreference: s.colorSchemePreference,
          highContrastEnabled: s.highContrastEnabled,
          reduceMotionEnabled: s.reduceMotionEnabled,
          customTokens: s.customTokens,
          directOAuthProviders: s.directOAuthProviders,
          authenticatedUserEmail: s.authenticatedUserEmail,
          notifications: s.notifications,
          unreadCount: s.unreadCount,
          emailUnreadCount: s.emailUnreadCount,
          onBehalfOfExpiresAt: s.onBehalfOfExpiresAt,
          onBehalfOfUser: s.onBehalfOfUser,
          // File (folder) password session tokens; expired ones are removed on first use (getFilePasswordSession / getFilePasswordSessionHeaderValue)
          filePasswordSessions: s.filePasswordSessions,
          folderContentStatsCache: s.folderContentStatsCache,
          filesThumbnails: s.filesThumbnails,
          fileThumbnailSkipUntil: s.fileThumbnailSkipUntil,
          // Open session stays ephemeral; only last user-managed height survives reload.
          viewerModalSizeVh: s.viewerModalSizeVh,
          appEvents: {
            activateTotp: {
              neverShowAgain: s.appEvents.activateTotp.neverShowAgain,
              remindAt: s.appEvents.activateTotp.remindAt,
            },
            ssoLogin: {
              isSsoLogin: s.appEvents.ssoLogin.isSsoLogin,
            },
            tooManyRequests: {
              lockedUntil: s.appEvents.tooManyRequests.lockedUntil,
            },
            fileShare: {
              isNewFilesShareRecipientsReadOnly:
                s.appEvents.fileShare.isNewFilesShareRecipientsReadOnly,
            },
            tagsList: {
              isExpanded: s.appEvents.tagsList.isExpanded,
            },
          },
        }),
      migrate: migrateAppPersistedState,
      onRehydrateStorage: () => (state) => {
        runAppStorePersistRehydration(state);
        // Re-arm the 429 lockout countdown if the persisted window is still in the future
        useAppStore.getState().appEvents.tooManyRequests.restoreFromPersistence();
      },
    },
  ),
);
